// Structured Data Schemas for SEO

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Cittasya®",
  url: "https://cittasya.com",
  logo: "https://cittasya.com/cittasya-logo.svg",
  description:
    "Yoga Nidra app for mental well-being, stress relief, and deep relaxation",
  foundingDate: "2024",
  sameAs: [
    "https://twitter.com/cittasya",
    "https://facebook.com/cittasya",
    "https://instagram.com/cittasya",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+1-XXX-XXX-XXXX",
    contactType: "customer service",
    availableLanguage: "English",
  },
};

export const webApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  name: "Cittasya®",
  description:
    "Transform your life with Cittasya® Yoga Nidra app. Experience guided deep relaxation practice, stress relief, better sleep, pain relief, and enhanced self-awareness through ancient Yoga Nidra techniques.",
  url: "https://cittasya.com",
  applicationCategory: "HealthApplication",
  operatingSystem: "iOS, Android, Web",
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
    availability: "https://schema.org/InStock",
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.8",
    ratingCount: "1000",
    bestRating: "5",
    worstRating: "1",
  },
  author: {
    "@type": "Organization",
    name: "Cittasya®",
    url: "https://cittasya.com",
  },
  screenshot: "https://cittasya.com/hero-1.png",
  featureList: [
    "Guided Yoga Nidra sessions",
    "Stress relief techniques",
    "Better sleep programs",
    "Pain relief meditation",
    "Mental wellness tracking",
    "Deep relaxation practices",
    "Enhanced self-awareness",
  ],
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Cittasya®",
  url: "https://cittasya.com",
  description:
    "Awaken Deep rest with Yoga Nidra - Deep Relaxation & Stress Relief",
  publisher: {
    "@type": "Organization",
    name: "Cittasya®",
  },
  potentialAction: {
    "@type": "SearchAction",
    target: "https://cittasya.com/search?q={search_term_string}",
    "query-input": "required name=search_term_string",
  },
};

export const breadcrumbSchema = (items: { name: string; url: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

export const articleSchema = (article: {
  title: string;
  description: string;
  url: string;
  image: string;
  publishedTime: string;
  modifiedTime?: string;
  author: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: article.title,
  description: article.description,
  url: article.url,
  image: article.image,
  datePublished: article.publishedTime,
  dateModified: article.modifiedTime || article.publishedTime,
  author: {
    "@type": "Organization",
    name: article.author,
  },
  publisher: {
    "@type": "Organization",
    name: "Cittasya®",
    logo: {
      "@type": "ImageObject",
      url: "https://cittasya.com/cittasya-logo.svg",
    },
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": article.url,
  },
});

export const faqSchema = (faqs: { question: string; answer: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
});

export const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Yoga Nidra Mental Wellness Sessions",
  description:
    "Professional guided Yoga Nidra sessions for stress relief, better sleep, pain relief, and mental wellness",
  provider: {
    "@type": "Organization",
    name: "Cittasya®",
  },
  serviceType: "Mental Health and Wellness",
  areaServed: "Worldwide",
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Yoga Nidra Programs",
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Stress Relief Yoga Nidra",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Better Sleep Program",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Pain Relief Sessions",
        },
      },
    ],
  },
};

export const reviewSchema = (
  reviews: { rating: number; comment: string; author: string; date: string }[]
) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: "Cittasya® Yoga Nidra App",
  review: reviews.map((review) => ({
    "@type": "Review",
    reviewRating: {
      "@type": "Rating",
      ratingValue: review.rating,
      bestRating: "5",
    },
    reviewBody: review.comment,
    author: {
      "@type": "Person",
      name: review.author,
    },
    datePublished: review.date,
  })),
});
